import { Injectable, inject, NgZone } from '@angular/core';
import moment from 'moment';
import $ from 'jquery';
import AngularHelperService from './angular-helper.service';

@Injectable({ providedIn: 'root' })
export class UiCalendarService {
	private angularHelperService: AngularHelperService = inject(AngularHelperService);
	
	extraEventSignature: (input: any) => any;
	eventSerialId: any = 1;
	sourceSerialId: any = 1;
	sourceEventsSerialId: any = 1;
	calendars = {};
	fullCalendarLoaded = false;
	fullCalendarReadyCallbacks: (() => void)[] = [];
	
	constructor(private zone: NgZone) {
		var _window: any = window;
		if (!_window.$) {
			_window.$ = $;
		}
		if (!_window.jQuery) {
			_window.jQuery = $;
		}
		// moment is attached to window in app.component.ts

		$.get('https://dqs3afosyxqoy.cloudfront.net/assets/common/js/fullcalendar-3.10.5.min.js',
			(fullCalendar) => {
				console.log("Full calendar loaded.");
				this.fullCalendarLoaded = true;
				this.fullCalendarReadyCallbacks.forEach(callback => callback());
			},
			(err) => {
				console.error('Failed to load fullcalendar.js.');
			}, 'script');
	}
	
	loadFullCalendar(callback: () => void) {
		if (this.fullCalendarLoaded) {
			callback();
		} else {
			this.fullCalendarReadyCallbacks.push(callback);
		}
	}
	
	init(extraEventSignature: (input: any) => any) {
		console.log("Calendar service init.");
		this.extraEventSignature = extraEventSignature;
		this.eventSerialId = 1;
		this.sourceSerialId = 1;
		this.sourceEventsSerialId = 1;
	}
	
	register(calendarId: string, newCalendar: any) {
		this.calendars[calendarId] = newCalendar;
	}
	
	unregister(calendarId: string) {
		this.calendars[calendarId] = '';
	}
	
	getById(calendarId: string) {
		return this.calendars[calendarId];
	}

	wrapFunctionWithZoneApply(fn: (...args: any[]) => any, zone: any) {
		return function(...args: any[]) {
			return zone.run(() => fn.apply(this, args));
		};
	}
	
	eventFingerprint = (e) => {
		if (!e._id) {
			e._id = this.eventSerialId++;
		}

		var extraSignature = this.extraEventSignature({
			event: e
		}) || '';
		var start = moment.isMoment(e.start) ? e.start.unix() : (e.start ? moment(e.start).unix() : '');
		var end = moment.isMoment(e.end) ? e.end.unix() : (e.end ? moment(e.end).unix() : '');

		// This extracts all the information we need from the event. http://jsperf.com/angular-calendar-events-fingerprint/3
		return [e._id, e.id || '', e.title || '', e.url || '', start, end, e.allDay || '', e.className || '', extraSignature].join('');
	}

	sourceFingerprint = (source) => {
		var fp = '' + (source.__id || (source.__id = this.sourceSerialId++));
		var events = this.angularHelperService.isObject(source) && source.events;

		if (events) {
			fp = fp + '-' + (events.__id || (events.__id = this.sourceEventsSerialId++));
		}
		return fp;
	}

	allEvents(sources) {
		return [].concat(
			(sources || []).reduce(
				(previous, source) => {
					if (this.angularHelperService.isArray(source)) {
						previous.push(source);
					} else if (this.angularHelperService.isObject(source) && this.angularHelperService.isArray(source.events)) {
						var extEvent = Object.keys(source).filter(
							(key) => {
								return (key !== '_id' && key !== 'events');
							}
						);

						source.events.forEach(
							(event) => {
								//this.angularHelperService.extend(event, extEvent);
								Object.assign(event, extEvent);
							}
						);

						previous.push(source.events);
					}
					return previous;
				},
				[]
			)
		);
	}

	changeWatcher(arraySource, tokenFn) {
		var self;
		var map = {};

		var getTokens = () => {
			return ((this.angularHelperService.isFunction(arraySource) ? arraySource() : arraySource) || []).reduce(
				(rslt, el) => {
					var token = tokenFn(el);
					map[token] = el;
					rslt.push(token);
					return rslt;
				},
				[]
			);
		}

		var subtractAsSets = (a, b) => {
			var obj = (b || []).reduce(
				(rslt, val) => {
					rslt[val] = true;
					return rslt;
				},
				Object.create(null)
			);
			return (a || []).filter(
				(val) => {
					return !obj[val];
				}
			);
		}

		var applyChanges = (newTokens, oldTokens) => {
			var i;
			var token;
			var replacedTokens = {};
			var removedTokens = subtractAsSets(oldTokens, newTokens);
			for (i = 0; i < removedTokens.length; i++) {
				var removedToken = removedTokens[i];
				var el = map[removedToken];
				delete map[removedToken];
				var newToken = tokenFn(el);
				// if the element wasn't removed but simply got a new token, its old token will be different from the current one
				if (newToken === removedToken) {
					self.onRemoved(el);
				} else {
					replacedTokens[newToken] = removedToken;
					self.onChanged(el);
				}
			}

			var addedTokens = subtractAsSets(newTokens, oldTokens);
			for (i = 0; i < addedTokens.length; i++) {
				token = addedTokens[i];
				if (!replacedTokens[token]) {
					self.onAdded(map[token]);
				}
			}
		};

		self = {
			subscribe: (onArrayChanged?: (newValue, oldValue) => boolean) => {
				var oldTokens: Array<any> = [];
				
				return this.angularHelperService.createInterval(() => {
					var newTokens = getTokens();
					//console.log("subscribe", newTokens);
					if (oldTokens.length !== newTokens.length) {
						oldTokens = newTokens;
						var notify = !(onArrayChanged && onArrayChanged(newTokens, oldTokens) === false);
						if (notify) {
							applyChanges(newTokens, oldTokens);
						}
					}
				}, 500);
			},
			onAdded: this.angularHelperService.noop,
			onChanged: this.angularHelperService.noop,
			onRemoved: this.angularHelperService.noop
		};
		return self;
	}

	getFullCalendarConfig(calendarSettings, uiCalendarConfig) {
		var config = {};
		
		Object.assign(config, uiCalendarConfig);
		Object.assign(config, calendarSettings);

		this.angularHelperService.forEach(config, (value, key) => {
			if (typeof value === 'function') {
				config[key] = this.wrapFunctionWithZoneApply(config[key], this.zone);
			}
		});

		return config;
	}

	getLocaleConfig(fullCalendarConfig) {
		if (!fullCalendarConfig.lang && !fullCalendarConfig.locale || fullCalendarConfig.useNgLocale) {
			// Configure to use locale names by default
			var tValues = (data) => {
				// convert {0: "Jan", 1: "Feb", ...} to ["Jan", "Feb", ...]
				return (Object.keys(data) || []).reduce(
					(rslt, el) => {
						rslt.push(data[el]);
						return rslt;
					},
					[]
				);
			};

			return {
				monthNames: tValues([
					"January",
					"February",
					"March",
					"April",
					"May",
					"June",
					"July",
					"August",
					"September",
					"October",
					"November",
					"December"
				]),
				monthNamesShort: tValues([
					"Jan",
					"Feb",
					"Mar",
					"Apr",
					"May",
					"Jun",
					"Jul",
					"Aug",
					"Sep",
					"Oct",
					"Nov",
					"Dec"
				]),
				dayNames: tValues([
					"Sunday",
					"Monday",
					"Tuesday",
					"Wednesday",
					"Thursday",
					"Friday",
					"Saturday"
				]),
				dayNamesShort: tValues([
					"Sun",
					"Mon",
					"Tue",
					"Wed",
					"Thu",
					"Fri",
					"Sat"
				])
			};
		}

		return {};
	}
}
